import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { currentBrand } from "@/lib/brand";
import { ShieldCheck } from "lucide-react";

export const Route = createFileRoute("/login/mfa")({
  component: MfaChallengePage,
});

function MfaChallengePage() {
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [factorId, setFactorId] = useState<string | null>(null);

  useEffect(() => {
    const loadFactor = async () => {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error || !data) {
        toast.error("Failed to load MFA factors. Please sign in again.");
        nav({ to: "/login" });
        return;
      }
      const totp = data.totp[0];
      if (!totp) {
        // No MFA factor — skip straight through
        nav({ to: "/app" });
        return;
      }
      setFactorId(totp.id);
    };
    loadFactor();
  }, [nav]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!factorId) return;
    setLoading(true);
    try {
      const { data: challenge, error: challengeErr } = await supabase.auth.mfa.challenge({
        factorId,
      });
      if (challengeErr || !challenge) throw challengeErr ?? new Error("Challenge failed");

      const { error: verifyErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code,
      });
      if (verifyErr) throw verifyErr;

      toast.success("2FA verified");
      nav({ to: "/app" });
    } catch (err: any) {
      toast.error(err?.message ?? "Incorrect code, please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-gradient-soft p-6">
      <div className="w-full max-w-md rounded-3xl border border-border bg-card p-8 shadow-soft">
        <div className="mb-6 flex items-center gap-3">
          <div className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-gradient-hero p-3 shadow-soft">
            <img
              src={currentBrand.logoSrc}
              alt={`${currentBrand.name} logo`}
              className="h-full w-auto"
            />
          </div>
          <div>
            <h1 className="text-xl font-bold">Two-factor authentication</h1>
            <p className="text-sm text-muted-foreground">
              Enter the 6-digit code from your authenticator app.
            </p>
          </div>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Authentication code</Label>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              required
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="rounded-2xl text-center text-lg tracking-widest"
              autoFocus
            />
          </div>
          <Button
            type="submit"
            disabled={loading || code.length < 6 || !factorId}
            className="w-full rounded-full bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90"
          >
            <ShieldCheck className="mr-2 h-4 w-4" />
            {loading ? "Verifying…" : "Verify"}
          </Button>
        </form>
        <p className="mt-4 text-center text-xs text-muted-foreground">
          Lost access to your authenticator?{" "}
          <a href="mailto:support@resoluteink.co.uk" className="font-semibold text-primary">
            Contact support
          </a>
        </p>
      </div>
    </div>
  );
}
